'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Plus, Star, Award, UserPlus, FileText, ChevronDown, ChevronUp, Edit2, Crown, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  getJungleStudentsAction, 
  upsertJungleStudentAction, 
  awardJunglePointsAction, 
  getJungleEventsAction,
  deleteJungleStudentAction,
  editJungleEventAction,
  deleteJungleEventAction
} from '@/app/actions/jungle-academy';

export type SchoolClassType = 'spanish' | 'art' | 'penalty';
export type JungleGroupId = 'eep' | 'group-a' | 'group-b' | 'group-c';

interface JungleStudent {
  id: string;
  name: string;
  pointsSpa: number;
  pointsArt: number;
  pointsPenalty?: number;
  totalPoints: number;
}

interface JungleClassEvent {
  id: string;
  studentId: string;
  className: SchoolClassType;
  event: string;
  date: string;
  points: number;
}

export default function JungleAcademyClient() {
  const [students, setStudents] = useState<JungleStudent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState<JungleGroupId | 'all'>('all');

  // Upsert Student State
  const [isUpsertStudentOpen, setIsUpsertStudentOpen] = useState(false);
  const [upsertStudentId, setUpsertStudentId] = useState<string | null>(null);
  const [upsertStudentName, setUpsertStudentName] = useState('');
  const [upsertStudentGroup, setUpsertStudentGroup] = useState<JungleGroupId>('eep');
  const [isUpserting, setIsUpserting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Award points state
  const [isAwardOpen, setIsAwardOpen] = useState(false);
  const [isPenaltyMode, setIsPenaltyMode] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [awardPoints, setAwardPoints] = useState(1);
  const [awardEvent, setAwardEvent] = useState('');
  const [awardClass, setAwardClass] = useState<SchoolClassType>('spanish');
  const [isAwarding, setIsAwarding] = useState(false);

  const togglePenaltyMode = (isPenalty: boolean) => {
    setIsPenaltyMode(isPenalty);
    if (isPenalty) {
       setAwardClass('penalty');
       if (awardPoints > 0) setAwardPoints(-Math.abs(awardPoints));
    } else {
       setAwardClass('spanish');
       if (awardPoints < 0) setAwardPoints(Math.abs(awardPoints));
    }
  };

  // Events View State
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [studentEvents, setStudentEvents] = useState<Record<string, JungleClassEvent[]>>({});
  const [isLoadingEvents, setIsLoadingEvents] = useState<Record<string, boolean>>({});
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingEventReason, setEditingEventReason] = useState('');
  const [editingEventClass, setEditingEventClass] = useState<SchoolClassType>('spanish');
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);

  const groups: { id: JungleGroupId | 'all'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'group-c', label: 'Group C' },
    { id: 'group-b', label: 'Group B' },
    { id: 'group-a', label: 'Group A' },
    { id: 'eep', label: 'EEP' },
  ];

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      const res = await getJungleStudentsAction(activeGroup);
      if (res.success) {
        setStudents(res.students || []);
      } else {
        console.error('Failed to fetch students:', res.error);
      }
    } catch (e) {
      console.error('Failed to fetch students:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [activeGroup]);

  const openUpsertModal = (student?: JungleStudent) => {
    if (student) {
      setUpsertStudentId(student.id);
      setUpsertStudentName(student.name);
      setUpsertStudentGroup(activeGroup === 'all' ? 'eep' : activeGroup);
    } else {
      setUpsertStudentId(null);
      setUpsertStudentName('');
      setUpsertStudentGroup(activeGroup === 'all' ? 'eep' : activeGroup);
    }
    setConfirmDelete(false);
    setIsUpsertStudentOpen(true);
  };

  const handleUpsertStudent = async () => {
    if (!upsertStudentName.trim()) return;
    setIsUpserting(true);
    try {
      const payload: any = { name: upsertStudentName, groupId: upsertStudentGroup };
      if (upsertStudentId) {
        payload.id = upsertStudentId;
      }

      const res = await upsertJungleStudentAction(payload);
      if (res.success) {
        setIsUpsertStudentOpen(false);
        if (activeGroup === 'all' || upsertStudentGroup === activeGroup) {
          fetchStudents();
        } else {
          setActiveGroup(upsertStudentGroup);
        }
      } else {
        console.error('Failed to upsert student:', res.error);
      }
    } catch (e) {
      console.error('Failed to upsert student:', e);
    } finally {
      setIsUpserting(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!upsertStudentId) return;
    setIsDeleting(true);
    try {
      const res = await deleteJungleStudentAction(upsertStudentId);
      if (res.success) {
        setIsUpsertStudentOpen(false);
        fetchStudents();
      } else {
        console.error('Failed to delete student:', res.error);
      }
    } catch (e) {
      console.error('Failed to delete student:', e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAwardPoints = async () => {
    if (!selectedStudentId || !awardEvent.trim() || awardPoints <= 0) return;
    setIsAwarding(true);
    try {
      const res = await awardJunglePointsAction({
        studentId: selectedStudentId,
        className: awardClass,
        event: awardEvent,
        points: awardPoints,
      });
      if (res.success) {
        setAwardEvent('');
        setAwardPoints(1);
        setIsAwardOpen(false);
        fetchStudents();
        // Clear events cache for this student to force reload on next expand
        setStudentEvents(prev => {
          const next = { ...prev };
          delete next[selectedStudentId];
          return next;
        });
      } else {
        console.error('Failed to award points:', res.error);
      }
    } catch (e) {
      console.error('Failed to award points:', e);
    } finally {
      setIsAwarding(false);
    }
  };

  const toggleStudentEvents = async (studentId: string) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      return;
    }

    setExpandedStudentId(studentId);

    // Fetch events if not already loaded
    if (!studentEvents[studentId]) {
      setIsLoadingEvents(prev => ({ ...prev, [studentId]: true }));
      try {
        const res = await getJungleEventsAction(studentId);
        if (res.success) {
          // Sort events newest first
          const events = res.events.sort((a: JungleClassEvent, b: JungleClassEvent) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          setStudentEvents(prev => ({ ...prev, [studentId]: events }));
        } else {
          console.error('Failed to fetch events:', res.error);
        }
      } catch (e) {
        console.error('Failed to fetch events:', e);
      } finally {
        setIsLoadingEvents(prev => ({ ...prev, [studentId]: false }));
      }
    }
  };

  const handleEditEvent = async (studentId: string) => {
    if (!editingEventId || !editingEventReason.trim()) return;
    setIsSavingEvent(true);
    try {
      const res = await editJungleEventAction({ eventId: editingEventId, newReason: editingEventReason, newClassName: editingEventClass });
      if (res.success) {
        setEditingEventId(null);
        setStudentEvents(prev => ({
          ...prev,
          [studentId]: prev[studentId].map(ev => 
            ev.id === editingEventId ? { ...ev, event: editingEventReason, className: editingEventClass } : ev
          )
        }));
        await fetchStudents();
      } else {
        console.error('Failed to edit event:', res.error);
      }
    } catch (e) {
      console.error('Failed to edit event:', e);
    } finally {
      setIsSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (studentId: string, eventId: string) => {
    setIsDeletingEvent(true);
    try {
      const res = await deleteJungleEventAction(eventId);
      if (res.success) {
        setDeletingEventId(null);
        setStudentEvents(prev => ({
          ...prev,
          [studentId]: prev[studentId].filter(ev => ev.id !== eventId)
        }));
        await fetchStudents();
      } else {
        console.error('Failed to delete event:', res.error);
      }
    } catch (e) {
      console.error('Failed to delete event:', e);
    } finally {
      setIsDeletingEvent(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-w-6xl mx-auto p-4 lg:p-8 space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black bg-gradient-to-r from-emerald-400 to-green-600 bg-clip-text text-transparent">
            Jungle Academy Leaderboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Evaluate and reward extraordinary student performance.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Dialog open={isUpsertStudentOpen} onOpenChange={setIsUpsertStudentOpen}>
            <button 
              onClick={() => openUpsertModal()}
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground font-semibold rounded-xl transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add Student
            </button>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{upsertStudentId ? 'Edit Student' : 'Add New Student'}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">Student Name</label>
                  <input
                    value={upsertStudentName}
                    onChange={e => setUpsertStudentName(e.target.value)}
                    placeholder="Enter full name"
                    className="px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ring-emerald-500/50 transition-all"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">Group</label>
                  <select
                    value={upsertStudentGroup}
                    onChange={e => setUpsertStudentGroup(e.target.value as JungleGroupId)}
                    className="px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ring-emerald-500/50"
                  >
                    {groups.filter(g => g.id !== 'all').map(g => (
                      <option key={g.id} value={g.id}>{g.label}</option>
                    ))}
                  </select>
                </div>
                {!confirmDelete ? (
                  <div className="flex gap-2 w-full mt-2">
                    <button
                      onClick={handleUpsertStudent}
                      disabled={isUpserting || isDeleting || !upsertStudentName.trim()}
                      className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
                    >
                      {isUpserting ? 'Saving...' : 'Save Student'}
                    </button>
                    {upsertStudentId && (
                      <button
                        onClick={() => setConfirmDelete(true)}
                        disabled={isUpserting || isDeleting}
                        className="flex-none px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors shadow-sm disabled:opacity-50"
                        title="Remove Student"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 bg-red-500/10 border border-red-500/50 p-3 rounded-xl shadow-sm mt-2">
                    <span className="text-xs text-red-500 text-center font-medium">Are you sure? This will remove the student permanently.</span>
                    <div className="flex gap-2 w-full mt-1">
                      <button
                        onClick={() => setConfirmDelete(false)}
                        className="flex-1 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-semibold"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteStudent}
                        disabled={isDeleting}
                        className="flex-1 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-sm disabled:opacity-50"
                      >
                        {isDeleting ? 'Removing...' : 'Yes, Remove'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isAwardOpen} onOpenChange={setIsAwardOpen}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-emerald-500/20">
                <Award className="w-4 h-4" />
                Points
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <div className="flex justify-between items-center pr-8">
                  <DialogTitle>{isPenaltyMode ? 'Apply Penalty' : 'Award Jungle Points'}</DialogTitle>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={!isPenaltyMode ? 'text-emerald-500 font-bold' : 'text-muted-foreground'}>Award</span>
                    <button 
                      onClick={() => togglePenaltyMode(!isPenaltyMode)}
                      className={`w-10 h-5 rounded-full relative transition-colors ${isPenaltyMode ? 'bg-red-500' : 'bg-emerald-500'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${isPenaltyMode ? 'right-0.5' : 'left-0.5'}`} />
                    </button>
                    <span className={isPenaltyMode ? 'text-red-500 font-bold' : 'text-muted-foreground'}>Penalty</span>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">Select Student</label>
                  <select
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value)}
                    className="px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ring-emerald-500/50"
                  >
                    <option value="">Select a student...</option>
                    {students.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.totalPoints || 0} pts)</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 flex-1">
                    <label className="text-sm font-semibold">{isPenaltyMode ? 'Category' : 'Class'}</label>
                    <select
                      value={awardClass}
                      onChange={e => setAwardClass(e.target.value as SchoolClassType)}
                      className={`px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ${isPenaltyMode ? 'ring-red-500/50' : 'ring-emerald-500/50'}`}
                      disabled={isPenaltyMode}
                    >
                      {!isPenaltyMode ? (
                        <>
                          <option value="spanish">Spanish</option>
                          <option value="art">Art</option>
                        </>
                      ) : (
                        <option value="penalty">Penalties</option>
                      )}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2 w-32">
                    <label className="text-sm font-semibold">Points</label>
                    <input
                      type="number"
                      max={isPenaltyMode ? "-1" : undefined}
                      min={!isPenaltyMode ? "1" : undefined}
                      value={awardPoints}
                      onChange={e => setAwardPoints(Number(e.target.value))}
                      className={`px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ${isPenaltyMode ? 'ring-red-500/50 text-red-500' : 'ring-emerald-500/50'}`}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">{isPenaltyMode ? 'Reason for Penalty' : 'Reason for Award'}</label>
                  <input
                    value={awardEvent}
                    onChange={e => setAwardEvent(e.target.value)}
                    placeholder={isPenaltyMode ? 'e.g. Disrupted class' : 'e.g. Extraordinary presentation'}
                    className={`px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 ${isPenaltyMode ? 'ring-red-500/50' : 'ring-emerald-500/50'}`}
                  />
                </div>

                <button
                  onClick={handleAwardPoints}
                  disabled={isAwarding || !selectedStudentId || !awardEvent.trim() || (isPenaltyMode ? awardPoints >= 0 : awardPoints <= 0)}
                  className={`w-full py-2.5 text-white font-bold rounded-lg disabled:opacity-50 transition-colors mt-2 flex items-center justify-center gap-2 ${isPenaltyMode ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                >
                  {isAwarding ? 'Processing...' : <><Star className="w-4 h-4" /> {isPenaltyMode ? 'Apply Penalty' : 'Grant Points'}</>}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 p-1 bg-muted/50 rounded-xl overflow-x-auto">
        {groups.map(group => (
          <button
            key={group.id}
            onClick={() => setActiveGroup(group.id)}
            className={`flex-1 min-w-[100px] px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
              activeGroup === group.id
                ? 'bg-background text-emerald-500 shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {group.label}
          </button>
        ))}
      </div>

      <Card className="flex flex-col flex-1 bg-background/50 border-border/50 overflow-hidden shadow-xl">
        <div className="grid grid-cols-[4rem_1fr_6rem_6rem_6rem_7rem_4rem] gap-4 p-4 border-b border-border/50 bg-muted/30 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <div className="text-center">Rank</div>
          <div>Student Name</div>
          <div className="text-right">Spa Pts</div>
          <div className="text-right">Art Pts</div>
          <div className="text-right">Penalty</div>
          <div className="text-right">Total Pts</div>
          <div className="text-right"></div>
        </div>

        <ScrollArea className="flex-1 h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-8 h-8 rounded-full border-4 border-emerald-500/30 border-t-emerald-500 animate-spin" />
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
              <Trophy className="w-12 h-12 mb-4 opacity-20" />
              <p className="text-lg font-semibold">No students yet</p>
              <p className="text-sm">Add students to {groups.find(g => g.id === activeGroup)?.label} to start tracking points!</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {(() => {
                const maxSpa = Math.max(1, ...students.map(s => s.pointsSpa || 0));
                const maxArt = Math.max(1, ...students.map(s => s.pointsArt || 0));
                
                let currentRank = 1;
                return students.map((student, index) => {
                  if (index > 0 && student.totalPoints < students[index - 1].totalPoints) {
                    currentRank += 1;
                  }
                  
                  return (
                <div key={student.id} className="border-b border-border/50 group relative">
                  {/* Subtle gradient hover effect behind the row */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 via-emerald-500/0 to-emerald-500/0 group-hover:from-emerald-500/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                  <div className="relative grid grid-cols-[4rem_1fr_6rem_6rem_6rem_7rem_4rem] gap-4 p-4 items-center hover:bg-muted/10 transition-colors">
                    <div className="flex justify-center relative">
                      {currentRank === 1 ? (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 text-white flex items-center justify-center font-black text-lg shadow-[0_0_20px_rgba(251,191,36,0.4)] border border-yellow-300/50">1</div>
                      ) : currentRank === 2 ? (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-300 to-slate-500 text-white flex items-center justify-center font-black text-lg shadow-md border border-slate-300/50">2</div>
                      ) : currentRank === 3 ? (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-600 to-amber-800 text-white flex items-center justify-center font-black text-lg shadow-md border border-amber-600/50">3</div>
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-muted text-muted-foreground flex items-center justify-center font-bold text-md border border-border/50">{currentRank}</div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-sm ring-2 ring-background">
                          {student.name.substring(0,2).toUpperCase()}
                        </div>
                        {currentRank === 1 && (
                          <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 rounded-full p-1 shadow-md">
                            <Crown className="w-3 h-3 fill-current" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <div className="font-black text-lg group-hover:text-emerald-500 transition-colors truncate">
                          {student.name}
                        </div>
                      </div>
                      <button 
                        onClick={() => openUpsertModal(student)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 bg-muted hover:bg-emerald-500/20 text-muted-foreground hover:text-emerald-500 rounded-md transition-all ml-auto shrink-0"
                        title="Edit student"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex justify-end items-center">
                      <div className={`font-bold text-base ${
                        (student.pointsSpa || 0) > 0 ? 'text-blue-500' : 'text-muted-foreground'
                      }`}>
                        {student.pointsSpa || 0}
                      </div>
                    </div>

                    <div className="flex justify-end items-center">
                      <div className={`font-bold text-base ${
                        (student.pointsArt || 0) > 0 ? 'text-blue-500' : 'text-muted-foreground'
                      }`}>
                        {student.pointsArt || 0}
                      </div>
                    </div>

                    <div className="flex justify-end items-center">
                      <div className={`font-bold text-base ${
                        (student.pointsPenalty || 0) > 0 ? 'text-red-500' : 'text-muted-foreground'
                      }`}>
                        {(student.pointsPenalty || 0) > 0 ? `-${student.pointsPenalty}` : '0'}
                      </div>
                    </div>
                    
                    <div className="text-right flex items-center justify-end">
                      <div className={`flex items-center justify-center px-4 py-1.5 rounded-full font-black text-lg shadow-sm border ${
                        currentRank === 1 ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/10 text-yellow-500 border-yellow-500/30' :
                        currentRank === 2 ? 'bg-gradient-to-r from-slate-500/20 to-slate-400/10 text-slate-400 border-slate-500/30' :
                        currentRank === 3 ? 'bg-gradient-to-r from-amber-700/20 to-amber-600/10 text-amber-600 border-amber-700/30' :
                        'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                      }`}>
                        {student.totalPoints || 0}
                      </div>
                    </div>

                    <div className="text-right">
                      <button
                        onClick={() => toggleStudentEvents(student.id)}
                        className={`p-2 rounded-xl transition-all ${
                          expandedStudentId === student.id 
                            ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' 
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                        }`}
                        title="View Events"
                      >
                        {expandedStudentId === student.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedStudentId === student.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden bg-background border-t border-border/30"
                      >
                        <div className="p-4 pl-[4.5rem] bg-gradient-to-r from-emerald-500/5 to-transparent">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5" /> Event History
                          </h4>
                          
                          {isLoadingEvents[student.id] ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <div className="w-4 h-4 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin" />
                              Loading events...
                            </div>
                          ) : !studentEvents[student.id] || studentEvents[student.id].length === 0 ? (
                            <div className="text-sm text-muted-foreground italic">No events recorded yet.</div>
                          ) : (
                            <div className="space-y-2 pr-4">
                              {studentEvents[student.id].map(event => (
                                <div key={event.id} className="flex items-start justify-between gap-4 p-3 bg-background border border-border/50 rounded-lg text-sm group/event">
                                  {deletingEventId === event.id ? (
                                    <div className="flex flex-col gap-2 w-full flex-1">
                                      <span className="text-sm font-medium text-red-500">Are you sure you want to delete this event?</span>
                                      <div className="flex gap-2">
                                        <button onClick={() => setDeletingEventId(null)} className="text-xs px-3 py-1.5 bg-muted hover:bg-muted/80 rounded transition-colors" disabled={isDeletingEvent}>Cancel</button>
                                        <button onClick={() => handleDeleteEvent(student.id, event.id)} disabled={isDeletingEvent} className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded transition-colors disabled:opacity-50">Delete</button>
                                      </div>
                                    </div>
                                  ) : editingEventId === event.id ? (
                                    <div className="flex flex-col gap-2 w-full flex-1 mr-4">
                                      <input
                                        value={editingEventReason}
                                        onChange={e => setEditingEventReason(e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded outline-none focus:ring-1 ring-emerald-500/50"
                                      />
                                      <select
                                        value={editingEventClass}
                                        onChange={e => setEditingEventClass(e.target.value as SchoolClassType)}
                                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded outline-none focus:ring-1 ring-emerald-500/50"
                                      >
                                        <option value="spanish">SPANISH</option>
                                        <option value="art">ART</option>
                                        <option value="penalty">PENALTY</option>
                                      </select>
                                      <div className="flex gap-2 justify-end">
                                        <button onClick={() => setEditingEventId(null)} className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded transition-colors">Cancel</button>
                                        <button onClick={() => handleEditEvent(student.id)} disabled={isSavingEvent} className="text-xs px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded transition-colors disabled:opacity-50">Save</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col flex-1">
                                      <div className="flex items-start gap-2">
                                        <span className="font-semibold leading-tight mt-0.5">{event.event}</span>
                                        <button 
                                          onClick={() => { setEditingEventId(event.id); setEditingEventReason(event.event); setEditingEventClass(event.className); }}
                                          className="opacity-0 group-hover/event:opacity-100 p-1 bg-muted hover:bg-emerald-500/10 text-muted-foreground hover:text-emerald-500 rounded transition-all shrink-0 mt-[-2px]" 
                                          title="Edit Reason"
                                        >
                                          <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button 
                                          onClick={() => setDeletingEventId(event.id)}
                                          className="opacity-0 group-hover/event:opacity-100 p-1 bg-muted hover:bg-red-500/10 text-muted-foreground hover:text-red-500 rounded transition-all shrink-0 mt-[-2px]" 
                                          title="Delete Event"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                      <span className="text-xs text-muted-foreground mt-1">
                                        {new Date(event.date).toLocaleDateString()} at {new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </span>
                                    </div>
                                  )}
                                  {deletingEventId !== event.id && (
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                      <span className={`font-bold ${event.points < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                                        {event.points > 0 ? '+' : ''}{event.points} pts
                                      </span>
                                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-muted">
                                        {event.className}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
              });
              })()}
            </div>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}
